import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { Check, ChevronDown, Search } from 'lucide-react';

import { cx } from '@/lib/cx';

import styles from './Combobox.module.css';

export type ComboboxOption = {
  value: string;
  label: string;
  /** Shown greyed beside the label (an offset, a code, a count). */
  meta?: string;
};

export type ComboboxProps = {
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  /** Shown when nothing is selected. */
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string | undefined;
  /**
   * How many matches to render at once.
   *
   * The time zone list is around 450 entries. Rendering all of them builds 450 DOM
   * nodes on every keystroke for a list nobody scrolls past the top of, so it stops
   * early and says so.
   */
  limit?: number;
  /** Supply server search results instead of filtering only the loaded page. */
  onSearch?: (query: string) => void;
};

/**
 * A select you can type into.
 *
 * Built rather than reached for: the native `<select>` cannot be filtered, and a list
 * of several hundred time zones is unusable without filtering.
 *
 * Follows the ARIA combobox pattern: a text input owning a listbox, with the active
 * option pointed at by `aria-activedescendant` rather than by moving focus. Focus
 * stays in the input the whole time, which is what lets typing and arrowing work
 * together.
 */
export function Combobox({
  value,
  onChange,
  options,
  placeholder = 'Search…',
  id,
  disabled = false,
  invalid = false,
  describedBy,
  limit = 100,
  onSearch,
}: ComboboxProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const listId = `${inputId}-list`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = options.find((option) => option.value === value);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (onSearch || !needle) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        option.value.toLowerCase().includes(needle),
    );
  }, [options, query, onSearch]);

  const shown = matches.slice(0, limit);
  const hidden = matches.length - shown.length;

  /**
   * Change the filter, and put the highlight back at the top.
   *
   * Together rather than in an effect watching `query`: the highlight cannot outlive
   * the list it points into (filtering to three matches while it sits at index 40
   * would leave nothing highlighted and Enter doing nothing), and typing is the event
   * that invalidates it, so this is where it belongs.
   */
  const updateQuery = (next: string) => {
    setQuery(next);
    onSearch?.(next);
    setActive(0);
  };

  // Keep the highlighted option in view while arrowing through a long list.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    updateQuery('');
  };

  const choose = (option: ComboboxOption) => {
    onChange(option.value);
    setOpen(false);
    updateQuery('');
    inputRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        openList();
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => {
        const next = current + step;
        if (next < 0) return shown.length - 1;
        if (next >= shown.length) return 0;
        return next;
      });
      return;
    }

    if (event.key === 'Home' && open) {
      event.preventDefault();
      setActive(0);
      return;
    }

    if (event.key === 'End' && open) {
      event.preventDefault();
      setActive(Math.max(0, shown.length - 1));
      return;
    }

    if (event.key === 'Enter' && open) {
      const option = shown[active];
      if (option) {
        // Only swallowed when it actually picks something, so Enter still submits
        // the surrounding form when the list is empty.
        event.preventDefault();
        choose(option);
      }
      return;
    }

    if (event.key === 'Escape' && open) {
      event.preventDefault();
      // Stopped here so a combobox inside a dialog closes itself first rather than
      // the dialog closing out from under it.
      event.stopPropagation();
      setOpen(false);
      updateQuery('');
    }
  };

  return (
    <div className={styles.root} ref={rootRef}>
      <div
        className={cx(styles.control, invalid && styles.invalid, disabled && styles.disabled)}
      >
        <span className={styles.leading} aria-hidden="true">
          <Search size={14} />
        </span>

        <input
          ref={inputRef}
          id={inputId}
          role="combobox"
          type="text"
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && shown[active] ? `${listId}-${active}` : undefined}
          aria-invalid={invalid ? true : undefined}
          aria-describedby={describedBy}
          className={styles.input}
          // Shows the selection when closed and the search while open, so the field
          // never looks empty just because it has focus.
          value={open ? query : (selected?.label ?? '')}
          placeholder={selected ? selected.label : placeholder}
          onChange={(event) => {
            updateQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={openList}
          onKeyDown={onKeyDown}
        />

        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          className={styles.toggle}
          aria-label={open ? 'Close the list' : 'Open the list'}
          onClick={() => {
            if (open) {
              setOpen(false);
            } else {
              openList();
              inputRef.current?.focus();
            }
          }}
        >
          <ChevronDown size={15} className={cx(styles.chevron, open && styles.chevronOpen)} />
        </button>
      </div>

      {open ? (
        <div className={styles.popover}>
          <ul className={styles.list} role="listbox" id={listId} ref={listRef}>
            {shown.map((option, index) => (
              <li key={option.value}>
                {/*
                  A div, not a button: inside a listbox the option carries the role,
                  and a nested button would be a second focus stop in a list of four
                  hundred. Selection is by pointer or by the keys above.
                */}
                <div
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={option.value === value}
                  data-active={index === active}
                  className={cx(
                    styles.option,
                    index === active && styles.optionActive,
                    option.value === value && styles.optionSelected,
                  )}
                  onPointerDown={(event) => {
                    // Before blur, or the list closes and the click lands on nothing.
                    event.preventDefault();
                    choose(option);
                  }}
                  onPointerEnter={() => {
                    setActive(index);
                  }}
                >
                  <span className={styles.optionLabel}>{option.label}</span>
                  {option.meta ? (
                    <span className={styles.optionMeta}>{option.meta}</span>
                  ) : null}
                  {option.value === value ? (
                    <Check size={14} className={styles.tick} aria-hidden="true" />
                  ) : null}
                </div>
              </li>
            ))}

            {shown.length === 0 ? (
              <li className={styles.empty}>Nothing matches that.</li>
            ) : null}
          </ul>

          {hidden > 0 ? (
            // Said plainly rather than silently truncated: a list that stops at a
            // hundred without saying so reads as "my zone is not here".
            <p className={styles.more} role="status">
              {hidden} more. Keep typing to narrow it down.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
